import React, { useEffect, useState } from 'react';
import { listenAssignments, fetchSchoolCalendar } from './api';
import { weekNumberForDate, todayIso } from './calendarMath';
import { weeklyCompletion, completionByWeekday, backlog } from './reporting';
import Bar from './Bar';

const WEEKDAY_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const LOOKBACK_WEEKS = 8;

// Completion trends / pacing / deferral (gap-review item 6) — plain CSS bars,
// no charting library, consistent with the rest of this codebase.
export default function TrendsTab({ familyData, student }) {
  const [assignments, setAssignments] = useState(null);
  const [calendar, setCalendar] = useState(undefined);

  useEffect(() => {
    if (!familyData?.id || !student?.studentEmail) return;
    const unsub = listenAssignments(familyData.id, student.studentEmail, setAssignments);
    fetchSchoolCalendar(familyData.id).then(setCalendar);
    return unsub;
  }, [familyData?.id, student?.studentEmail]);

  if (assignments === null || calendar === undefined) return <p className="settings-note">Loading…</p>;
  if (!calendar?.weeks?.length) return <p className="settings-note">No school calendar seeded yet.</p>;

  const currentWeek = weekNumberForDate(calendar.weeks, todayIso());

  const weeklyStats = weeklyCompletion(assignments, { currentWeek, lookback: LOOKBACK_WEEKS });
  const byWeekday = completionByWeekday(assignments);
  const completedCount = byWeekday.reduce((a, b) => a + b, 0);
  const maxWeekday = Math.max(1, ...byWeekday);

  // ── Deferred/backlog — not-complete items whose scheduled date has
  // already passed, grouped by the week they were due ──
  const { byWeek: backlogByWeek, weeks: backlogWeeks, total: totalBacklog } =
    backlog(assignments, todayIso());

  return (
    <div>
      <div className="form-label" style={{ marginTop: 12 }}>Weekly completion (last {weeklyStats.length} active weeks)</div>
      {weeklyStats.length === 0 ? (
        <p className="settings-note">No data yet.</p>
      ) : (
        weeklyStats.map(w => (
          <Bar key={w.weekNumber} label={`Week ${w.weekNumber}${w.weekNumber === currentWeek ? ' (this week)' : ''}`}
            pct={w.pct} sublabel={`${w.complete}/${w.total}`} />
        ))
      )}

      <div className="form-label" style={{ marginTop: 20 }}>When work actually gets done</div>
      {completedCount === 0 ? (
        <p className="settings-note">Nothing completed yet.</p>
      ) : (
        <>
          {[1, 2, 3, 4, 5].map(dow => (
            <Bar key={dow} label={WEEKDAY_LABELS[dow]} pct={(byWeekday[dow] / maxWeekday) * 100} sublabel={byWeekday[dow]} />
          ))}
          {(byWeekday[0] + byWeekday[6]) > 0 && (
            <p className="settings-note">Plus {byWeekday[0] + byWeekday[6]} completed on a weekend.</p>
          )}
        </>
      )}

      <div className="form-label" style={{ marginTop: 20 }}>Backlog — scheduled but not done, past due</div>
      {totalBacklog === 0 ? (
        <p className="settings-note">Nothing overdue.</p>
      ) : (
        <>
          <p className="settings-note" style={{ color: 'var(--bad, #b00)' }}>{totalBacklog} item(s) overdue, across {backlogWeeks.length} week(s).</p>
          {backlogWeeks.map(wn => (
            <Bar key={wn} label={`Week ${wn}`} pct={100} sublabel={`${backlogByWeek[wn]} overdue`} />
          ))}
        </>
      )}
    </div>
  );
}
